import {
  ShiftStatus,
  Order,
  OrderAux,
  OrderReadiness,
} from '@/models/order.model';
import { HttpService } from './http.service';
import { nextTick } from 'vue/types/umd';

export class OrderService extends HttpService {
  protected _connectedWS: WebSocket | null = null;
  protected _connectedWSWatcher: number | null = null;
  protected _connectedYear: number;
  protected _connectedMonth: number;
  protected _connectedOnShiftChange: ((data: ShiftStatus[]) => void) | null = null;

  constructor() {
    super();
    this._connectedYear = 0;
    this._connectedMonth = 0;
  }

  public async GetAux(): Promise<OrderAux> {
    return await this.get<OrderAux>('/Orders/Aux/', []);
  }

  public async GetShift(year: number, month: number): Promise<ShiftStatus[]> {
    return await this.get<ShiftStatus[]>('/Orders/Shifts/', [
      ['year', `${year}`],
      ['month', `${month}`],
    ]);
  }

  public async GetOrdersOfCustomer(
    customer: string,
    yearmonth: string
  ): Promise<Order[]> {
    console.log({ customer, yearmonth });

    return await this.get<Order[]>('/Orders/Customer', [
      ['customer', customer],
      ['yearmonth', yearmonth],
    ]);
  }

  public async GetOrdersOfShift(date: string): Promise<Order[]> {
    return await this.get<Order[]>('/Orders/Shift', [['ordersShift', date]]);
  }

  public async GetRelevantOrdersOfShift(date: string): Promise<Order[]> {
    return await this.get<Order[]>('/Orders/RelevantForShift', [
      ['ordersShift', date],
    ]);
  }

  async GetOrdersReadiness(orderIds: string[]): Promise<OrderReadiness[]> {
    return await this.put<OrderReadiness[], string[]>(
      '/Orders/Readiness',
      [],
      orderIds
    );
  }

  public async GetResolvedDays(): Promise<string[]> {
    return await this.get<string[]>('/Orders/ResolvedDays', []);
  }

  public async GetOrdersResolvedAt(day: string): Promise<Order[]> {
    return await this.get<Order[]>(`/Orders/ResolvedAt`, [['day', day]]);
  }

  public async AddOrder(Order: Order): Promise<Order> {
    return await this.put<Order, Order>('/Orders/', [], Order);
  }

  public async UpdateOrder(Order: Order): Promise<string> {
    return await this.put<string, Order>('/Orders/Update/', [], Order);
  }

  public async DeleteOrder(OrderId: string): Promise<string> {
    return await this.delete('/Orders/', [['OrderId', OrderId]]);
  }

  public async LockDate(target: {
    shift: string;
    lockStatus: boolean;
  }): Promise<string> {
    return await this.put(
      '/Orders/Lock/',
      [
        ['targetDateTime', target.shift],
        ['lock', target.lockStatus ? 'false' : 'true'],
      ],
      ''
    );
  }

  public async GetShiftStatusWS(
    year: number,
    month: number,
    onShiftChange: (data: ShiftStatus[]) => void
  ): Promise<WebSocket> {
    const baseUrl = HttpService._baseURL.replace(/^https?:\/\//, '');
    const ws = new WebSocket(`ws${HttpService._baseURL.startsWith('https') ? 's' : ''}://${baseUrl}/Connect/shifts`);
    ws.onopen = () => {
      ws.send(JSON.stringify({
        type: 'authenticate',
        token: HttpService._accessToken
      }));
    };
    this._connectedYear = year;
    this._connectedMonth = month;
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === 'ping') {
        return;
      }
      if (msg.type === 'authenticated') {
        ws.send(JSON.stringify({
          action: 'subscribe',
          year: this._connectedYear,
          month: this._connectedMonth
        }));
        return;
      }
      if (msg.type === 'unsubscribed') {
        return;
      }
      if (msg.topic.startsWith('shifts-')) {
        onShiftChange?.(msg.data);
        return;
      }
      console.log('[GetShiftStatusWS] Message received from WebSocket:', msg);
    };
    this._connectedWS = ws;
    this._connectedOnShiftChange = onShiftChange;
    if (!this._connectedWSWatcher) {
      this._connectedWSWatcher = window.setInterval(() => {
        if (!this._connectedWS || this._connectedWS.readyState !== WebSocket.CLOSED) {
          return;
        }
        this.GetShiftStatusWS(this._connectedYear, this._connectedMonth, this._connectedOnShiftChange!);
      }, 3000);
    }
    return ws;
  }

  public async ChangeShiftStatusWS(year: number, month: number, onShiftChange: (data: ShiftStatus[]) => void): Promise<WebSocket> {
    if (!this._connectedWS) {
      return this.GetShiftStatusWS(year, month, onShiftChange);
    }
    if (this._connectedYear !== year || this._connectedMonth !== month) {
      this._connectedWS.send(JSON.stringify({
        action: 'unsubscribe',
        year: this._connectedYear,
        month: this._connectedMonth
      }));
      this._connectedWS.send(JSON.stringify({
        action: 'subscribe',
        year: year,
        month: month
      }));
      this._connectedYear = year;
      this._connectedMonth = month;
    }
    return this._connectedWS;
  }

  public CloseShiftStatusWS(): void {
    if (this._connectedWS) {
      this._connectedWSWatcher && window.clearInterval(this._connectedWSWatcher);
      this._connectedWSWatcher = null;
      this._connectedWS.close();
      this._connectedWS = null;
      this._connectedYear = 0;
      this._connectedMonth = 0;
    }
  }
}
